import React, {
  useState,
  useEffect,
  useRef,
  forwardRef,
  useMemo,
  useCallback,
  ReactNode,
} from "react";
import styled from "styled-components";
import { useRouter } from "next/router";
import {
  Button,
  colors,
  font,
  media,
  Icon,
  IconName,
} from "@literati/public-ui-shared-react";
import { debounce } from "lodash";
import analytics from "analytics";
import useHandleOutsideClick from "utils/hooks/useHandleOutsideClick";
import { School, ClassroomAffiliate } from "lib/api/affiliates";
import {
  useAffiliateSearch,
  AFFILIATE_SEARCH_SCHOOL,
  AFFILIATE_SEARCH_TEACHER,
} from "utils/hooks/useAffiliateSearch";
import {
  Affiliate,
  AffiliateType,
  isClassroomAffiliate,
  isSchoolWithAffiliateId,
} from "components/SchoolBox/schoolBoxTypes";

const ERROR_MESSAGE: Record<string, ReactNode> = {
  [AFFILIATE_SEARCH_SCHOOL]: "Please select a school from the list.",
  [AFFILIATE_SEARCH_TEACHER]: "Please select your referral from the list.",
} as const;

const FormContainer = styled.div`
  max-width: 570px;
  margin: 2.5rem auto 0;
  position: relative;
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;

  ${media.sm} {
    gap: 0;
    flex-direction: row;
    align-content: stretch;
  }
`;

const InputGroup = styled.div`
  height: 56px;
  padding: 0 1.7rem;
  background: ${colors.white};
  border: 1px solid ${colors.newsprint4}; 
  position: relative;
  flex-grow: 1;
  flex-shrink: 1;
  border-radius: 56px;

  ${media.sm} {
    border-radius: 28px 0 0 28px;
  }

  strong {
    top: 50%;
    left: 1.25rem;
    display: flex;
    align-items: center;
    gap: 0.5rem;
    position: absolute;
    transform: translateY(-50%);
  }

  label {
    color: ${colors.warmGray3};
    top: 0.5rem;
    left: 1.7rem; 
    position: absolute;
    transform: scale(0.7);
    transform-origin left top;
    display: block;
    z-index: 0;
    transition: all 0.6s;
  }

  input {
    width: 100%;
    padding-top: 1.4rem;
    background: transparent;
    appearance: none;
    position: relative;
    z-index: 1;
    transition: padding 0.3s;

    &::placeholder {
      color: transparent;
    }

    &:placeholder-shown ~ label {
      top: 1rem;
      transform: scale(1);
      transition: all 0.3s;
    }

    &:placeholder-shown {
      padding-top: 1rem;
      transition: padding 0.3s;
    }
  }

  &[data-error="true"] {
    border-color: ${colors.errorRed};

    label {
      color: ${colors.errorRed};
    }
  }
`;

const StyledIcon = styled(Icon)`
  width: 1.375rem;
  height: 1.375rem;
  display: inline-block;
`;

const ContinueButton = styled(Button)`
  height: 56px;
  color: ${colors.inkBlack};
  background: ${colors.tealShade1};
  width: 100%;
  flex-shrink: 0;
  flex-grow: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: ${font.weight.heavy};
  transition: all 0.3s;
  border-radius: 56px;

  ${media.sm} {
    max-width: 195px;
    border-radius: 0 28px 28px 0;
  }

  ${media.hover} {
    &:hover:enabled {
      color: ${colors.tealShade3};
      background: #95dfdd;
    }

    &&::after {
      border-color: transparent;
    }
  }
`;

const ResultsListContainer = styled.div`
  width: 100%;
  top: 56px;
  left: 0;
  margin-top: 0.5rem;
  padding: 0 1rem;
  position: absolute;
  z-index: 3;

  ${media.lg} {
    top: 100%;
    padding: 0 1.75rem;
  }
`;

const ResultsListInner = styled.div`
  max-height: 230px;
  background: ${colors.white};
  border-radius: 8px;
  overflow-x: hidden;
  filter: drop-shadow(0px 2px 8px rgba(0, 0, 0, 0.1));

  ${media.lg} {
    max-height: 300px;
  }

  ul {
    text-align: left;
  }

  li {
    padding: 1rem;
    font-size: 0.875rem;
    line-height: 1.25rem;
    display: flex;
    flex-direction: column;
    cursor: pointer;

    ${media.lg} {
      padding: 1rem 1.25rem;
    }

    strong {
      font-weight: ${font.weight.heavy};
    }

    &:hover {
      background-color: ${colors.frenchBlueTint4};
    }
  }
`;

const ResultsListFooter = styled.div`
  padding: 1.5rem 1.25rem;
  background: ${colors.papierBlanc};
  font-size: 0.875rem;
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const LinkButton = styled.button`
  color: ${colors.tealShade3};
  font-weight: ${font.weight.heavy};
`;

const Spinner = styled(Icon)`
  margin: 1rem auto;
`;

const SubmissionErrorMessage = styled.p`
  text-align: left;
  padding: 0.5rem 0 0 1.75rem;
  color: ${colors.errorRed};
  font-size: 0.875rem;
`;

const ResultsList = forwardRef(
  (
    {
      isFetching,
      results,
      type,
      onSelect,
      onLoadMore,
    }: {
      isFetching?: boolean;
      results: Array<School> | Array<ClassroomAffiliate>;
      type: string;
      onSelect: (item: School | ClassroomAffiliate) => void;
      onLoadMore: () => void;
    },
    ref: any
  ) => {
    const debouncedOnLoadMore = useMemo(() => debounce(onLoadMore, 250), []);

    const handleScroll = (e: any) => {
      const target = e.target;
      if (
        target.scrollHeight - target.scrollTop <
        target.clientHeight + Math.max(80, target.scrollHeight * 0.1)
      ) {
        debouncedOnLoadMore();
      }
    };

    const router = useRouter();
    const skipRoute = useCallback(() => {
      router.push(`/subscribe`);
    }, []);

    return (
      <ResultsListContainer ref={ref}>
        <ResultsListInner onScroll={handleScroll}>
          {isFetching ? (
            <Spinner name={IconName.LOADING} />
          ) : (
            <ul>
              {results.map((item) => {
                let itemContent;
                let id;

                if (AFFILIATE_SEARCH_SCHOOL === type) {
                  const school = item as School;
                  id = school?.id;
                  itemContent = (
                    <>
                      {school?.name && <strong>{school.name}</strong>}
                      {school?.city && (
                        <span>
                          {school.city}, {school.state}
                        </span>
                      )}
                    </>
                  );
                } else {
                  const teacher = item as ClassroomAffiliate;
                  id = teacher?.uid;
                  itemContent = (
                    <>
                      {teacher?.name && <strong>{teacher.name}</strong>}
                      {teacher?.classroom && (
                        <span>{teacher.classroom.schoolData?.name}</span>
                      )}
                    </>
                  );
                }

                return (
                  <li
                    key={id}
                    data-testid={id}
                    onClick={() => {
                      onSelect(item);
                    }}
                  >
                    {itemContent}
                  </li>
                );
              })}
            </ul>
          )}
          {AFFILIATE_SEARCH_SCHOOL === type ? (
            <ResultsListFooter>
              <span>Not seeing your school?</span>
              <LinkButton onClick={skipRoute}>Add School Later</LinkButton>
            </ResultsListFooter>
          ) : null}
        </ResultsListInner>
      </ResultsListContainer>
    );
  }
);

export interface SchoolBoxAffiliationSearchProps {
  autoFocusInput?: boolean;
  errorMessage?: ReactNode;
  initialSelectedItem?: Affiliate | null;
  onSubmit: (affiliateId: string) => void;
  type?: AffiliateType;
}

export const SchoolBoxAffiliationSearch = ({
  autoFocusInput = true,
  errorMessage,
  initialSelectedItem,
  onSubmit,
  type = AFFILIATE_SEARCH_SCHOOL,
}: SchoolBoxAffiliationSearchProps) => {
  const [query, setQuery] = useState("");
  const [showResults, setShowResults] = useState(false);
  const [selectedItem, setSelectedItem] = useState<
    Affiliate | null | undefined
  >(initialSelectedItem);
  const [isError, setIsError] = useState(false);
  const [isSubmissionError, setIsSubmissionError] = useState(false);
  const [inputFocused, setInputFocused] = useState(autoFocusInput);

  const { results, error, loadMore, isFetching } = useAffiliateSearch({
    query,
    type,
  });

  // prevent clearing of input for prefill value
  const isMounted = useRef(false);

  useEffect(() => {
    if (isMounted.current) {
      setSelectedItem(null);
      setIsError(false);
      setQuery("");
    } else {
      isMounted.current = true;
    }
  }, [type]);

  useEffect(() => {
    if (results?.length && !selectedItem) {
      setShowResults(true);
    }
  }, [results, selectedItem]);

  useEffect(() => {
    setIsError(error);
  }, [results, error]);

  useEffect(() => {
    setShowResults(false);
    setIsError(false);
    setIsSubmissionError(false);
  }, [type]);

  useEffect(() => {
    if (isFetching) {
      setShowResults(true);
    }
  }, [isFetching, setShowResults]);

  const resultsListRef = useRef();

  // Close the modal if clicked outside
  const closeResultsList = () => setShowResults(false);
  useHandleOutsideClick(
    resultsListRef,
    closeResultsList,
    showResults && !inputFocused
  );

  const handleOnChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const inputValue = e.target?.value;
    setQuery(inputValue);
    setIsSubmissionError(false);
  };

  const handleInputBlur = () => setInputFocused(false);
  const handleInputFocus = () => {
    setInputFocused(true);
    setShowResults(true);
  };

  const debouncedOnChange = useMemo(() => debounce(handleOnChange, 250), []);

  const handleLoadMore = useCallback(() => {
    loadMore(true);
  }, [loadMore]);

  const handleOnSelect = async (item: Affiliate) => {
    setSelectedItem(item);
    setShowResults(false);
  };

  const handleOnSubmit = () => {
    analytics.trackAmplitudeClickEvent("Continue", selectedItem?.name);
    if (selectedItem) {
      if (isClassroomAffiliate(selectedItem)) {
        const id = (selectedItem.shortCode || selectedItem.uid) as string;
        return onSubmit(id);
      } else if (isSchoolWithAffiliateId(selectedItem)) {
        return onSubmit(selectedItem.defaultEducatorClassroomAffiliateCode);
      } else {
        setIsSubmissionError(true);
      }
    } else {
      setIsSubmissionError(true);
    }
  };

  const inputGroup = useMemo(() => {
    const name = AFFILIATE_SEARCH_SCHOOL === type ? "school" : "teacher";
    const placeholder =
      AFFILIATE_SEARCH_SCHOOL === type
        ? "Search for your school"
        : "Enter your teacher’s referral code";

    if (selectedItem) {
      const name =
        "classroom" in selectedItem
          ? selectedItem?.classroom?.teacherName || selectedItem?.name
          : selectedItem?.name;
      return (
        <InputGroup data-testid="input-group-item-selected">
          <strong>
            <StyledIcon name={IconName.CHECKFILL} /> {name}
          </strong>
        </InputGroup>
      );
    } else {
      return (
        <InputGroup data-error={isError || isSubmissionError}>
          <input
            autoComplete="off"
            key={`input-${type}`}
            type="text"
            name={name}
            className="text-input"
            placeholder={placeholder}
            onBlur={handleInputBlur}
            onChange={debouncedOnChange}
            onFocus={handleInputFocus}
            autoFocus={autoFocusInput}
          />
          <label>{placeholder}</label>
        </InputGroup>
      );
    }
  }, [
    type,
    selectedItem,
    isError,
    isSubmissionError,
    debouncedOnChange,
    autoFocusInput,
  ]);

  return (
    <FormContainer>
      <FormGroup>
        {inputGroup}
        <ContinueButton onClick={handleOnSubmit}>Continue</ContinueButton>
      </FormGroup>
      {isSubmissionError ? (
        <SubmissionErrorMessage>
          {errorMessage || ERROR_MESSAGE[type]}
        </SubmissionErrorMessage>
      ) : null}
      {showResults && (
        <ResultsList
          ref={resultsListRef}
          isFetching={isFetching}
          type={type}
          results={results as any}
          onSelect={handleOnSelect}
          onLoadMore={handleLoadMore}
        />
      )}
    </FormContainer>
  );
};
